import React from 'react'
import { connect } from 'react-redux'
import {
  Table2,
  PopupProvider,
  ListButton,
  MoreButton,
  RoundShadowButton,
  AddToPlaylistTransformer
} from 'components'
import { playNextLogo, playAtEndLogo } from 'images'
import ReleaseActions, { thunks as ReleaseThunks } from 'reducers/Releases'
import PropTypes from 'prop-types'
import PlayQueueActions, { thunks as PlayQueueThunks } from 'reducers/PlayQueue'
import { thunks as DownloadThunks } from 'reducers/Downloads'
import { withTranslation } from 'react-i18next'
import { formatMilliSeconds, getStringFromSeconds } from 'utils/dateHelpers'
import stringHelpers from 'utils/stringHelpers'
import { browserHelpers } from 'utils'
import placeholderLogo from 'images/icons/placeholder.png'
import {
  AnchorPositions,
  PopupDirections,
  SongTableColumns
} from 'constants/enums'
import { PLAY_TRIGGERS } from 'constants/tracking'
import formatters from './Formatters'

const mapStateToProps = ({ global, market }) => ({
  markets: market?.markets,
  isIpadView: global.isIpadView
})

@connect(mapStateToProps)
class SongsTable extends React.PureComponent {
  static propTypes = {
    showMarkets: PropTypes.bool,
    controlledHover: PropTypes.bool,
    showPlayQueueOptions: PropTypes.bool,
    columns: PropTypes.arrayOf(PropTypes.string),
    items: PropTypes.arrayOf(PropTypes.object),
    markets: PropTypes.arrayOf(PropTypes.object),
    downloadSource: PropTypes.string,
    playSource: PropTypes.string,

    t: PropTypes.func,
    renderModal: PropTypes.func,
    onHoverStop: PropTypes.func,
    dispatch: PropTypes.func.isRequired,
    isIpadView: PropTypes.bool
  }
  static defaultProps = {
    controlledHover: false,
    downloadSource: 'songs_table'
  }

  handleShowModal = (song) => {
    const { dispatch } = this.props
    dispatch(
      ReleaseThunks.getDetail(song.releaseid, (releaseDetail) => {
        dispatch(ReleaseActions.toggleReleaseInModal(releaseDetail))
      })
    )
  }

  handlePlaySong = (song) => {
    const { dispatch, playSource } = this.props
    dispatch(
      PlayQueueThunks.playSong(song, {
        source: playSource,
        trigger: PLAY_TRIGGERS.ROW
      })
    )
  }

  handleDownloadSong = (song) => {
    const { dispatch, downloadSource } = this.props
    dispatch(
      DownloadThunks.addSongToDownloadQueue(song, {
        source: downloadSource
      })
    )
  }

  handlePlayNext = (song) => {
    const { dispatch } = this.props
    dispatch(PlayQueueActions.insertItemNext(song))
  }

  handlePlayAtTheBottom = (song) => {
    const { dispatch } = this.props
    dispatch(PlayQueueActions.insertItemAtBottom(song))
  }

  insertIds = () => {
    const { items } = this.props
    this._itemsWithIds = items.map((item, i) => ({
      ...item,
      id: item.trackid,
      index: i + 1 + '.'
    }))
  }

  getColumns = () => {
    const { t, markets, showMarkets, columns, isIpadView } = this.props
    const { limitLength } = stringHelpers
    const flexClasses = 'flex-shrink-0'
    const options = [
      {
        name: SongTableColumns.index,
        title: '',
        // width: 70,
        field: 'index',
        cellClassName: 'text-left'
      },
      {
        name: SongTableColumns.coverart,
        image: true,
        src: 'coverart',
        cellStyleName: 'release-coverart',
        fallback: placeholderLogo
      },
      {
        name: SongTableColumns.markets,
        // title: t('release-list.markets'),
        // width: 100,
        field: 'market_tags',
        cellStyleName: 'release-market',
        cellClassName: 'flex-none',
        format: (field) => formatters.formatMarketsCell(markets, field)
      },
      {
        name: SongTableColumns.artist,
        title: t('release-list.artist'),
        // width: isIpadView ? 190 : 150,
        sort: 'artist',
        field: 'artist',
        resizable: true,
        cellStyleName: 'release-artist',
        cellClassName: `truncate ${flexClasses}`
      },
      {
        name: SongTableColumns.title,
        title: t('release-list.title'),
        // width: isIpadView ? 240 : 200,
        sort: 'title',
        field: 'title',
        resizable: true,
        cellStyleName: 'release-title',
        cellClassName: `truncate ${flexClasses}`
      },
      {
        name: SongTableColumns.duration,
        title: t('release-list.duration'),
        // width: 120,
        sort: 'duration',
        field: 'duration',
        resizable: true,
        cellStyleName: 'release-duration',
        cellClassName: `${flexClasses}`,
        format: (field) => getStringFromSeconds(field)
      },
      {
        name: SongTableColumns.available,
        title: t('release-list.available-date'),
        // width: 170,
        sort: 'available',
        field: 'available',
        cellStyleName: 'release-date',
        cellClassName: `truncate ${flexClasses}`,
        resizable: true,
        format: (field) => formatMilliSeconds(field)
      },
      {
        name: SongTableColumns.labels,
        title: t('release-list.label'),
        // width: 200,
        field: 'labels',
        cellStyleName: 'release-label',
        cellClassName: `break-words ${flexClasses}`,
        resizable: true,
        format: (field) => formatters.formatLabelCell(field)
      },
      {
        name: SongTableColumns.genres,
        title: t('release-list.formats'),
        // width: 120,
        field: 'genres',
        cellStyleName: 'release-format',
        cellClassName: `break-words ${flexClasses}`,
        resizable: true,
        format: (field) => limitLength(field.join(', '), 30)
      }
    ]

    const processedOptions = options.filter(
      (item) => showMarkets || item.name !== SongTableColumns.markets
    )
    if (columns === undefined) {
      return processedOptions
    } else {
      return options.filter((item) => columns.includes(item.name))
    }
  }

  renderModal = (song) => {
    const { t, showPlayQueueOptions } = this.props
    const { candownload } = song
    return (
      <PopupProvider
        tapOutSideToDismiss
        anchorPosition={AnchorPositions.bottomLeft}
        popupDirection={PopupDirections.downLeft}
        renderTrigger={({ setOnOff }) => (
          <MoreButton
            onClick={(event) => {
              event.stopPropagation()
              setOnOff(true)
            }}
          />
        )}
        renderChildren={({ setOnOff }) => (
          <div className="bg-white rounded text-black overflow-y-auto max-h-64 scrollbar-blue w-40">
            <AddToPlaylistTransformer
              showPlayQueueOptions={showPlayQueueOptions}
              song={song}
              setOnOff={setOnOff}
              previousOptions={
                <>
                  <ListButton
                    render={() => {
                      return (
                        <div className="flex flex-row items-center justify-start w-full">
                          <RoundShadowButton
                            removeTextComponent
                            icon={playNextLogo}
                            cicleWidthClass="w-4"
                            imageWidthClass="w-2"
                          />
                          <p className="text-xs px-2">
                            {t('general.play-next')}
                          </p>
                        </div>
                      )
                    }}
                    onClick={() => this.handlePlayNext(song)}
                  />
                  <ListButton
                    render={() => {
                      return (
                        <div className="flex flex-row items-center justify-start w-full">
                          <RoundShadowButton
                            removeTextComponent
                            icon={playAtEndLogo}
                            cicleWidthClass="w-4"
                            imageWidthClass="w-2"
                          />
                          <p className="text-xs px-2">
                            {t('general.play-later')}
                          </p>
                        </div>
                      )
                    }}
                    onClick={() => this.handlePlayAtTheBottom(song)}
                  />
                  {candownload && browserHelpers.browserAllowsDownload && (
                    <ListButton
                      name={t('general.download')}
                      onClick={() => this.handleDownloadSong(song)}
                    />
                  )}
                </>
              }
            />
          </div>
        )}
      />
    )
  }

  render() {
    this.insertIds()
    const { renderModal, onHoverStop, controlledHover } = this.props
    const items = this._itemsWithIds

    return (
      <div>
        <Table2
          items={items}
          controlledHover={controlledHover}
          tableId="songs"
          // columns={showMarkets ? columns : columns.slice(0, -1)}
          columns={this.getColumns()}
          renderModal={renderModal || this.renderModal}
          onHoverStop={onHoverStop}
          onClick={this.handleShowModal}
          onPlay={this.handlePlaySong}
        />
      </div>
    )
  }
}

export default withTranslation()(SongsTable)
